import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHeader } from '../../context/HeaderContext';
import { getCampaignDetails } from '../../api/campaign';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdArrowBack } from 'react-icons/md';
import { FaClipboardList, FaTag, FaChartBar, FaUsers, FaMoneyBillWave, FaDollarSign, FaCreditCard, FaGift, FaBullseye, FaTicketAlt, FaLink, FaCalendarAlt, FaFileAlt, FaChartLine, FaStickyNote, FaTrophy, FaMedal, FaImage, FaBox, FaPercent, FaLeaf, FaTree, FaAd, FaCheck, FaClock, FaExchangeAlt } from 'react-icons/fa';
import Chip from '../../components/Chip';

const CampaignDetails = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const { id } = useParams();
  const { partnerType } = useSelector((state) => state.partner);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);

  const isNGO = partnerType === 'ngo';

  useEffect(() => {
    setHeaderTitle('Campaign Details');
    if (id) {
      fetchCampaignDetails();
    }
  }, [setHeaderTitle, id]);

  const fetchCampaignDetails = async () => {
    try {
      setLoading(true);
      const data = await getCampaignDetails(id);
      setCampaign(data);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      toast.error('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `₹${amount}`;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className='p-6'>
        <div className='text-center py-20'>
          <p className='text-lg text-gray-500'>Campaign not found</p>
          <button
            onClick={() => navigate('/campaigns/active')}
            className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark'
          >
            Back to Campaigns
          </button>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </div>
    );
  }

  const campaignType = (campaign.campaignType || campaign.type || '').toLowerCase();
  const isChallenge = campaignType.includes('challenge');
  const isBrandProduct = campaignType.includes('brand') || campaignType.includes('product');
  const isAdvertisement = campaignType.includes('advertisement') || campaignType.includes('ad');
  const isPlantTrees = campaignType.includes('tree') || campaignType.includes('plant');

  // Get KPI metrics from campaign data
  const kpiMetrics = campaign.kpiMetrics || campaign.impact || {};

  // Helper to format discount
  const formatDiscount = (type, value) => {
    if (!value && value !== 0) return 'N/A';
    if (type === 'percentage' || type === 'Percentage') {
      return `${value}%`;
    }
    return `₹${value}`;
  };

  return (
    <div className='w-full pb-6'>
      {/* Header with Back Button and Title */}
      <div className='bg-white rounded-md shadow-sm mb-4 px-6 py-4'>
        <button
          onClick={() => navigate(-1)}
          className='flex items-center gap-2 text-primary hover:text-primaryHoverDark mb-3 font-medium text-sm'
        >
          <MdArrowBack className='text-lg' />
          Back
        </button>

        <div className='flex justify-between items-start'>
          <h1 className='text-2xl font-bold text-gray-900'>
            {campaign.title || campaign.campaignTitle || 'Campaign Details'}
          </h1>
          <Chip value={campaign.status || 'Active'} color="green" variant="filled" />
        </div>
      </div>

      {/* Main Content Grid - Two columns */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        {/* Left Column - Main Content */}
        <div className='lg:col-span-2 space-y-4'>

          {/* Campaign Overview */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>Campaign Overview</h2>

            <div className='space-y-3'>
              <div className='flex items-start gap-2'>
                <span className='text-gray-600 text-sm w-40 flex-shrink-0 flex items-center gap-2'><FaClipboardList className='text-primary' /> Title</span>
                <span className='text-gray-900 text-sm font-medium flex-1'>
                  {campaign.title || campaign.campaignTitle || 'N/A'}
                </span>
              </div>

              <div className='flex items-start gap-2'>
                <span className='text-gray-600 text-sm w-40 flex-shrink-0 flex items-center gap-2'><FaTag className='text-primary' /> Campaign Type</span>
                <span className='text-gray-900 text-sm flex-1'>
                  {campaign.campaignType || campaign.type || 'N/A'}
                </span>
              </div>

              <div className='flex items-start gap-2'>
                <span className='text-gray-600 text-sm w-40 flex-shrink-0 flex items-center gap-2'><FaChartBar className='text-primary' /> Status</span>
                <div className='flex-1'>
                  <Chip value={campaign.status || 'Active'} color="green" variant="filled" small />
                </div>
              </div>

              <div className='flex items-start gap-2'>
                <span className='text-gray-600 text-sm w-40 flex-shrink-0 flex items-center gap-2'><FaUsers className='text-primary' /> Sponsor Type</span>
                <span className='text-gray-900 text-sm flex-1'>
                  {campaign.sponsorType || 'Ngo'}
                </span>
              </div>
            </div>
          </div>

          {/* Budget & Pricing */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>Budget & Pricing</h2>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaMoneyBillWave className='text-primary' />
                  <span className='text-gray-600 text-sm'>Campaign Budget</span>
                </div>
                <p className='text-gray-900 font-medium'>{formatCurrency(campaign.budget || campaign.campaignBudget)}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaDollarSign className='text-primary' />
                  <span className='text-gray-600 text-sm'>Rate Value</span>
                </div>
                <p className='text-gray-900 font-medium'>{formatCurrency(campaign.rateValue || campaign.budget)}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaCreditCard className='text-primary' />
                  <span className='text-gray-600 text-sm'>Pricing Model</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.pricingModel || 'Cost per Redemption'}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaTag className='text-primary' />
                  <span className='text-gray-600 text-sm'>Rate Type</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.rateType || 'Fixed'}</p>
              </div>
            </div>
          </div>

          {/* Challenge Details (only for challenge campaigns) */}
          {isChallenge && (
            <div className='bg-white rounded-md shadow-sm p-6'>
              <h2 className='text-lg font-semibold text-gray-900 mb-4'>Challenge Details</h2>

              <div className='space-y-3'>
                <div className='flex items-start gap-2'>
                  <span className='text-gray-600 text-sm w-40 flex-shrink-0'>🎯 Challenge Name</span>
                  <span className='text-gray-900 text-sm flex-1'>
                    {campaign.challengeName || campaign.title || 'N/A'}
                  </span>
                </div>

                <div className='flex items-start gap-2'>
                  <span className='text-gray-600 text-sm w-40 flex-shrink-0'>🎖️ Total Points</span>
                  <div className='flex-1'>
                    <div className='text-gray-900 text-sm font-medium'>
                      {campaign.totalPoints || campaign.impact?.totalPoints || '60 points'}
                    </div>
                    <div className='text-xs text-gray-500 mt-1'>
                      Beer: {campaign.pointsBreakdown?.beer || '20'} | Bronze: {campaign.pointsBreakdown?.bronze || '3'} | Carbon: {campaign.pointsBreakdown?.carbon || '40'}
                    </div>
                  </div>
                </div>

                <div className='flex items-start gap-2'>
                  <span className='text-gray-600 text-sm w-40 flex-shrink-0'>📝 Challenge Description</span>
                  <div className='text-gray-900 text-sm flex-1'>
                    {campaign.challengeDescription || campaign.description || 'This challenge is to test the Unique coupons for Sponsor Challenge Campaign'}
                  </div>
                </div>

                {campaign.challengeCoverImage && (
                  <div className='flex items-start gap-2'>
                    <span className='text-gray-600 text-sm w-40 flex-shrink-0'>🖼️ Challenge Cover Image</span>
                    <div className='flex-1'>
                      <img
                        src={campaign.challengeCoverImage}
                        alt="Challenge Cover"
                        className='w-32 h-32 object-cover rounded-lg border border-gray-200'
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attached Products/Services */}
          {campaign.attachedProducts && campaign.attachedProducts.length > 0 && (
            <div className='bg-white rounded-md shadow-sm p-6'>
              <h2 className='text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4'>
                <span className='text-blue-600'>📦</span> Attached Products/Services
              </h2>

              <div className='grid grid-cols-1 gap-4'>
                {campaign.attachedProducts.map((product, index) => (
                  <div key={index} className='border border-gray-200 rounded-lg p-4'>
                    <div className='flex items-center gap-4'>
                      {product.image && (
                        <img
                          src={product.image}
                          alt={product.name || 'Product'}
                          className='w-20 h-20 object-cover rounded-md'
                        />
                      )}
                      <div className='flex-1'>
                        <h3 className='font-semibold text-gray-900'>{product.name || 'Yummiez Crispy Bite'}</h3>
                        <div className='flex items-center gap-2 mt-1'>
                          <Chip value={product.status || 'Product'} color="blue" variant="outlined" small />
                        </div>
                      </div>
                      <div className='text-sm text-gray-500'>
                        Status: {product.status || 'Active'}
                      </div>
                      <div className='text-sm text-gray-500'>
                        Added: {formatDateOnly(product.addedDate || campaign.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reward Configuration */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4'>
              <span className='text-purple-600'>🎁</span> Reward Configuration
            </h2>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaGift className='text-primary' />
                  <span className='text-gray-600 text-sm'>Reward Type</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.rewardType || 'Discount/Coupon'}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaExchangeAlt className='text-primary' />
                  <span className='text-gray-600 text-sm'>Redemption Type</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.redemptionType || 'N/A'}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaPercent className='text-primary' />
                  <span className='text-gray-600 text-sm'>Discount Type</span>
                </div>
                <p className='text-gray-900 font-medium capitalize'>{campaign.typeOfDiscount || 'N/A'}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaDollarSign className='text-primary' />
                  <span className='text-gray-600 text-sm'>Discount Amount</span>
                </div>
                <p className='text-gray-900 font-medium'>{formatDiscount(campaign.typeOfDiscount, campaign.discountValue)}</p>
              </div>

              <div className='col-span-2 space-y-1'>
                <div className='flex items-center gap-2'>
                  <FaTicketAlt className='text-primary' />
                  <span className='text-gray-600 text-sm'>Coupon Code Format</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.couponCodeFormat || 'Unique'}</p>
              </div>

              {campaign.linkToUseCoupon && (
                <div className='col-span-2 space-y-1'>
                  <div className='flex items-center gap-2'>
                    <FaLink className='text-primary' />
                    <span className='text-gray-600 text-sm'>Link to Use Coupon</span>
                  </div>
                  <a
                    href={campaign.linkToUseCoupon || '#'}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:text-blue-800 text-sm break-all underline'
                  >
                    {campaign.linkToUseCoupon}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Coupon Statistics - Show for Brand/Product and Challenge campaigns (hide for NGO) */}
          {(isBrandProduct || isChallenge) && !isNGO && (
            <div className='bg-white rounded-md shadow-sm p-6'>
              <h2 className='text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4'>
                <span className='text-yellow-600'>🎟️</span> Coupon Statistics
              </h2>

              <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                <div className='bg-gray-50 rounded-lg p-4 text-center border border-gray-200'>
                  <p className='text-2xl font-bold text-fontBlack'>
                    {kpiMetrics.totalCoupons || campaign.maxCouponCount || 0}
                  </p>
                  <p className='text-sm text-gray-600 mt-1'>Total Coupons</p>
                </div>
                <div className='bg-green-50 rounded-lg p-4 text-center border border-green-200'>
                  <p className='text-2xl font-bold text-green-600'>
                    {kpiMetrics.availableCoupons || 0}
                  </p>
                  <p className='text-sm text-gray-600 mt-1'>Available</p>
                </div>
                <div className='bg-blue-50 rounded-lg p-4 text-center border border-blue-200'>
                  <p className='text-2xl font-bold text-blue-600'>
                    {kpiMetrics.assignedCoupons || 0}
                  </p>
                  <p className='text-sm text-gray-600 mt-1'>Assigned</p>
                </div>
                <div className='bg-yellow-50 rounded-lg p-4 text-center border border-yellow-200'>
                  <p className='text-2xl font-bold text-yellow-600'>
                    {kpiMetrics.redeemedCoupons || campaign.totalRedemptions || 0}
                  </p>
                  <p className='text-sm text-gray-600 mt-1'>Redeemed</p>
                </div>
              </div>

              {/* Additional Coupon Info */}
              <div className='grid grid-cols-2 gap-4 mt-4'>
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <FaCheck className='text-green-500' />
                    <span className='text-gray-600 text-sm'>Max Coupon Count</span>
                  </div>
                  <p className='text-gray-900 font-medium'>{campaign.maxCouponCount || 'Unlimited'}</p>
                </div>
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <FaClock className='text-orange-500' />
                    <span className='text-gray-600 text-sm'>Max Redemption per User</span>
                  </div>
                  <p className='text-gray-900 font-medium'>{campaign.maxRedemptionCount || 1}</p>
                </div>
              </div>

              {/* Coupon Redemption Instructions */}
              {campaign.couponRedemptionInstructions && (
                <div className='mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center gap-2 mb-2'>
                    <FaFileAlt className='text-primary' />
                    <span className='text-gray-600 text-sm font-medium'>Redemption Instructions</span>
                  </div>
                  <p className='text-gray-700 text-sm'>{campaign.couponRedemptionInstructions}</p>
                </div>
              )}
            </div>
          )}

          {/* Payment & Billing */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>Payment & Billing</h2>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <span className='text-gray-600 text-sm'>📅 Invoice Frequency</span>
                </div>
                <p className='text-gray-900 font-medium'>{campaign.invoiceFrequency || 'Weekly'}</p>
              </div>

              <div className='space-y-1'>
                <div className='flex items-center gap-2'>
                  <span className='text-gray-600 text-sm'>📝 Payment Terms</span>
                </div>
                <p className='text-gray-900 font-medium'>Net {campaign.paymentTerms || '15'}</p>
              </div>
            </div>
          </div>

          {/* Reporting */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>Reporting</h2>

            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <span className='text-gray-600 text-sm'>📊 Report Frequency</span>
              </div>
              <p className='text-gray-900 font-medium'>{campaign.reportFrequency || 'Weekly'}</p>
            </div>
          </div>

          {/* Notes */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h2 className='text-lg font-semibold text-gray-900 mb-4'>Notes</h2>

            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <span className='text-gray-600 text-sm'>📋 dfhdfh</span>
              </div>
              <p className='text-gray-600 text-sm'>{campaign.notes || ''}</p>
            </div>
          </div>

        </div>

        {/* Right Column - Timeline & Campaign Info */}
        <div className='lg:col-span-1 space-y-4'>

          {/* Timeline */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h3 className='font-semibold text-gray-900 mb-4'>Timeline</h3>

            <div className='space-y-3'>
              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-green-500 rounded-full mt-1.5'></div>
                <div className='flex-1'>
                  <div className='text-xs text-gray-600'>Start Date</div>
                  <div className='text-sm font-medium text-gray-900'>{formatDate(campaign.startDate)}</div>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-red-500 rounded-full mt-1.5'></div>
                <div className='flex-1'>
                  <div className='text-xs text-gray-600'>End Date</div>
                  <div className='text-sm font-medium text-gray-900'>{formatDate(campaign.endDate)}</div>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-blue-500 rounded-full mt-1.5'></div>
                <div className='flex-1'>
                  <div className='text-xs text-gray-600'>Created</div>
                  <div className='text-sm font-medium text-gray-900'>{formatDate(campaign.createdAt)}</div>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <div className='w-2 h-2 bg-orange-500 rounded-full mt-1.5'></div>
                <div className='flex-1'>
                  <div className='text-xs text-gray-600'>Last Updated</div>
                  <div className='text-sm font-medium text-gray-900'>{formatDate(campaign.updatedAt)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Info */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h3 className='font-semibold text-gray-900 mb-4'>Campaign Info</h3>

            <div className='space-y-2'>
              <div>
                <div className='text-xs text-gray-600'>Campaign ID</div>
                <div className='text-xs font-mono text-gray-900 break-all'>{campaign._id || campaign.id || id}</div>
              </div>

              <div>
                <div className='text-xs text-gray-600 mb-1'>Active Status</div>
                <Chip value={campaign.status || 'Active'} color="green" variant="filled" small />
              </div>
            </div>
          </div>

          {/* Impact Metrics */}
          <div className='bg-white rounded-md shadow-sm p-6'>
            <h3 className='font-semibold text-gray-900 mb-4'>Impact Metrics</h3>

            <div className='space-y-3'>
              {/* Common metrics */}
              <div className='space-y-1'>
                <div className='text-xs text-gray-600'>Total Participants</div>
                <div className='text-2xl font-bold text-green-600'>
                  {kpiMetrics.totalParticipants || kpiMetrics.uniqueUsers || campaign.impact?.totalParticipants || 0}
                </div>
              </div>

              <div className='space-y-1'>
                <div className='text-xs text-gray-600'>Carbon Savings (kg)</div>
                <div className='text-2xl font-bold text-green-600'>
                  {kpiMetrics.totalCarbonSavingsKg || kpiMetrics.totalCarbonSavings || campaign.totalCarbonSavings || 0}
                </div>
              </div>

              {/* Challenge-specific metrics */}
              {isChallenge && (
                <>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Total Submissions</div>
                    <div className='text-2xl font-bold text-purple-600'>
                      {kpiMetrics.totalSubmissions || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Challenge Points</div>
                    <div className='text-2xl font-bold text-blue-600'>
                      {kpiMetrics.totalChallengePoints || 0}
                    </div>
                  </div>
                </>
              )}

              {/* Brand/Product-specific metrics */}
              {isBrandProduct && !isNGO && (
                <>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Impressions</div>
                    <div className='text-2xl font-bold text-blue-600'>
                      {kpiMetrics.totalImpressions || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Revenue Generated</div>
                    <div className='text-2xl font-bold text-green-600'>
                      ₹{kpiMetrics.totalRevenueGenerated || campaign.totalRevenueGenerated || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Conversion Rate</div>
                    <div className='text-2xl font-bold text-orange-600'>
                      {kpiMetrics.conversionRate || campaign.conversionRate || 0}%
                    </div>
                  </div>
                </>
              )}

              {/* Advertisement-specific metrics */}
              {isAdvertisement && (
                <>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Total Impressions</div>
                    <div className='text-2xl font-bold text-blue-600'>
                      {kpiMetrics.totalImpressions || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Unique Impressions</div>
                    <div className='text-2xl font-bold text-purple-600'>
                      {kpiMetrics.uniqueImpressions || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Click Through Rate</div>
                    <div className='text-2xl font-bold text-red-600'>
                      {kpiMetrics.clickThroughRate || 0}%
                    </div>
                  </div>
                </>
              )}

              {/* Plant Trees-specific metrics */}
              {isPlantTrees && (
                <>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Trees Planted</div>
                    <div className='text-2xl font-bold text-green-600'>
                      {kpiMetrics.totalTreesPlanted || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Certificates Issued</div>
                    <div className='text-2xl font-bold text-blue-600'>
                      {kpiMetrics.totalCertificatesIssued || 0}
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <div className='text-xs text-gray-600'>Points Donated</div>
                    <div className='text-2xl font-bold text-purple-600'>
                      {kpiMetrics.totalPointsDonated || 0}
                    </div>
                  </div>
                </>
              )}

              {/* Coupons Issued - Hide for NGO */}
              {!isNGO && (isBrandProduct || isChallenge) && (
                <div className='space-y-1'>
                  <div className='text-xs text-gray-600'>Coupons Issued</div>
                  <div className='text-2xl font-bold text-yellow-600'>
                    {kpiMetrics.totalCouponsIssued || kpiMetrics.assignedCoupons || campaign.impact?.couponsIssued || 0}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default CampaignDetails;
